import type { IIntegrationAdminRepository } from '@yawi/domain';
import type {
    CreateIntegrationDto,
    CreateWebhookDto,
    Integration,
    IntegrationLog,
    IntegrationLogFilters,
    IntegrationTestResult,
    IntegrationType,
    PaginatedResult,
    UpdateIntegrationDto,
    UpdateWebhookDto,
    WebhookConfig,
    WebhookDelivery,
} from '@yawi/types';
import { getApiClient } from '../../http/apiClient';

const BASE_URL = 'v1/admin/integrations';
const WEBHOOKS_URL = 'v1/webhooks';

export class ApiIntegrationAdminRepository implements IIntegrationAdminRepository {
  // ============ INTEGRATIONS ============

  async getIntegrations(type?: IntegrationType): Promise<Integration[]> {
    const client = getApiClient();
    const url = type ? `${BASE_URL}?type=${type}` : BASE_URL;
    const response = await client.get<Integration[]>(url);
    return response;
  }

  async getIntegrationById(id: string): Promise<Integration> {
    const client = getApiClient();
    const response = await client.get<Integration>(`${BASE_URL}/${id}`);
    return response;
  }

  async createIntegration(data: CreateIntegrationDto): Promise<Integration> {
    const client = getApiClient();
    const response = await client.post<Integration>(BASE_URL, data);
    return response;
  }

  async updateIntegration(id: string, data: UpdateIntegrationDto): Promise<Integration> {
    const client = getApiClient();
    const response = await client.patch<Integration>(`${BASE_URL}/${id}`, data);
    return response;
  }

  async deleteIntegration(id: string): Promise<void> {
    const client = getApiClient();
    await client.delete(`${BASE_URL}/${id}`);
  }

  async testIntegration(id: string): Promise<IntegrationTestResult> {
    const client = getApiClient();
    const response = await client.post<IntegrationTestResult>(`${BASE_URL}/${id}/test`);
    return response;
  }

  async enableIntegration(id: string): Promise<Integration> {
    const client = getApiClient();
    const response = await client.post<Integration>(`${BASE_URL}/${id}/enable`);
    return response;
  }

  async disableIntegration(id: string): Promise<Integration> {
    const client = getApiClient();
    const response = await client.post<Integration>(`${BASE_URL}/${id}/disable`);
    return response;
  }

  // ============ INTEGRATION LOGS ============

  async getLogs(filters?: IntegrationLogFilters): Promise<PaginatedResult<IntegrationLog>> {
    const client = getApiClient();
    const params = new URLSearchParams();
    if (filters?.type) params.append('type', filters.type);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.integrationId) params.append('integrationId', filters.integrationId);
    if (filters?.startDate) params.append('startDate', filters.startDate);
    if (filters?.endDate) params.append('endDate', filters.endDate);
    if (filters?.page) params.append('page', filters.page.toString());
    if (filters?.limit) params.append('limit', filters.limit.toString());

    const queryString = params.toString();
    const url = queryString ? `${BASE_URL}/logs?${queryString}` : `${BASE_URL}/logs`;

    const response = await client.get<PaginatedResult<IntegrationLog>>(url);
    return response;
  }

  async getLogById(id: string): Promise<IntegrationLog> {
    const client = getApiClient();
    const response = await client.get<IntegrationLog>(`${BASE_URL}/logs/${id}`);
    return response;
  }

  // ============ WEBHOOKS ============

  async getWebhooks(): Promise<WebhookConfig[]> {
    const client = getApiClient();
    const response = await client.get<WebhookConfig[]>(WEBHOOKS_URL);
    return response;
  }

  async getMyWebhooks(): Promise<WebhookConfig[]> {
    const client = getApiClient();
    const response = await client.get<WebhookConfig[]>(`${WEBHOOKS_URL}/my`);
    return response;
  }

  async getWebhookById(id: string): Promise<WebhookConfig> {
    const client = getApiClient();
    const response = await client.get<WebhookConfig>(`${WEBHOOKS_URL}/${id}`);
    return response;
  }

  async createWebhook(data: CreateWebhookDto): Promise<WebhookConfig> {
    const client = getApiClient();
    const response = await client.post<WebhookConfig>(WEBHOOKS_URL, data);
    return response;
  }

  async updateWebhook(id: string, data: UpdateWebhookDto): Promise<WebhookConfig> {
    const client = getApiClient();
    const response = await client.put<WebhookConfig>(`${WEBHOOKS_URL}/${id}`, data);
    return response;
  }

  async deleteWebhook(id: string): Promise<void> {
    const client = getApiClient();
    await client.delete(`${WEBHOOKS_URL}/${id}`);
  }

  async testWebhook(id: string): Promise<IntegrationTestResult> {
    const client = getApiClient();
    const response = await client.post<IntegrationTestResult>(`${WEBHOOKS_URL}/${id}/test`);
    return response;
  }

  async getWebhookDeliveries(webhookId: string, page = 1, limit = 20): Promise<PaginatedResult<WebhookDelivery>> {
    const client = getApiClient();
    const response = await client.get<PaginatedResult<WebhookDelivery>>(
      `${WEBHOOKS_URL}/${webhookId}/deliveries?page=${page}&limit=${limit}`
    );
    return response;
  }
}
